import { useUserProfile } from "@/hooks/useStore";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useApp } from "@/App";
import {
	ArrowLeft,
	Flame,
	MessageSquare,
	GitFork,
	ArrowBigUp,
	Trophy,
	Calendar,
	Star,
} from "lucide-react";

function getRank(reputation: number): { label: string; color: string } {
	if (reputation >= 2000) return { label: "Elite Analyst", color: "text-amber-400 border-amber-400/30 bg-amber-400/10" };
	if (reputation >= 1500) return { label: "Senior Analyst", color: "text-purple-400 border-purple-400/30 bg-purple-400/10" };
	if (reputation >= 1000) return { label: "Analyst", color: "text-blue-400 border-blue-400/30 bg-blue-400/10" };
	if (reputation >= 500) return { label: "Contributor", color: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10" };
	return { label: "Newcomer", color: "text-zinc-400 border-zinc-400/30 bg-zinc-400/10" };
}

export function ProfilePage({ userId }: { userId: string }) {
	const { profile, loading } = useUserProfile(userId);
	const { navigate } = useApp();

	if (loading || !profile) {
		return (
			<div className="py-20 text-center text-muted-foreground">
				Loading profile...
			</div>
		);
	}

	const rank = getRank(profile.reputation);
	const joinDate = new Date(profile.createdAt).toLocaleDateString("en-US", {
		month: "long",
		year: "numeric",
	});

	return (
		<div className="space-y-6">
			<button
				onClick={() => navigate({ name: "leaderboard" })}
				className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
			>
				<ArrowLeft className="h-4 w-4" />
				Back to leaderboard
			</button>

			{/* Profile Header */}
			<Card className="border-border/40 bg-card/50">
				<CardContent className="p-6">
					<div className="flex items-start gap-5">
						<div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-full bg-orange-500/20 text-3xl font-bold text-orange-500">
							{profile.username[0].toUpperCase()}
						</div>
						<div className="min-w-0 flex-1 space-y-2">
							<div className="flex items-center gap-3">
								<h1 className="text-2xl font-bold">{profile.username}</h1>
								<Badge variant="outline" className={rank.color}>
									{rank.label}
								</Badge>
							</div>
							{profile.bio && (
								<p className="text-sm text-muted-foreground">{profile.bio}</p>
							)}
							<div className="flex items-center gap-2 text-xs text-muted-foreground">
								<Calendar className="h-3.5 w-3.5" />
								Joined {joinDate}
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Reputation Score */}
			<Card className="border-orange-500/20 bg-orange-500/5">
				<CardContent className="p-5">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<Trophy className="h-8 w-8 text-orange-500" />
							<div>
								<p className="text-sm text-muted-foreground">Reputation Score</p>
								<p className="text-3xl font-black text-orange-400">
									{profile.reputation.toLocaleString()}
								</p>
							</div>
						</div>
						<div className="flex items-center gap-1 rounded-full border border-orange-500/20 bg-orange-500/10 px-3 py-1">
							<Star className="h-4 w-4 text-orange-500" />
							<span className="text-sm font-medium text-orange-400">{rank.label}</span>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Stats Grid */}
			<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
				<Card className="border-border/40 bg-card/50">
					<CardContent className="p-4 text-center">
						<Flame className="mx-auto mb-1 h-5 w-5 text-orange-500" />
						<p className="text-2xl font-bold">{profile.roastCount}</p>
						<p className="text-xs text-muted-foreground">Roasts</p>
					</CardContent>
				</Card>
				<Card className="border-border/40 bg-card/50">
					<CardContent className="p-4 text-center">
						<MessageSquare className="mx-auto mb-1 h-5 w-5 text-blue-400" />
						<p className="text-2xl font-bold">{profile.commentCount}</p>
						<p className="text-xs text-muted-foreground">Comments</p>
					</CardContent>
				</Card>
				<Card className="border-border/40 bg-card/50">
					<CardContent className="p-4 text-center">
						<GitFork className="mx-auto mb-1 h-5 w-5 text-emerald-400" />
						<p className="text-2xl font-bold">{profile.rewriteCount}</p>
						<p className="text-xs text-muted-foreground">Rewrites</p>
					</CardContent>
				</Card>
				<Card className="border-border/40 bg-card/50">
					<CardContent className="p-4 text-center">
						<ArrowBigUp className="mx-auto mb-1 h-5 w-5 text-amber-400" />
						<p className="text-2xl font-bold">{profile.upvotesReceived}</p>
						<p className="text-xs text-muted-foreground">Upvotes</p>
					</CardContent>
				</Card>
			</div>

			{/* How reputation works */}
			<Card className="border-border/40 bg-card/30">
				<CardContent className="p-5">
					<h3 className="mb-3 font-semibold">How Reputation Works</h3>
					<div className="space-y-2 text-sm text-muted-foreground">
						<div className="flex items-center justify-between">
							<span>Submit a roast</span>
							<Badge variant="outline" className="text-xs">+10 pts</Badge>
						</div>
						<Separator />
						<div className="flex items-center justify-between">
							<span>Leave a comment</span>
							<Badge variant="outline" className="text-xs">+5 pts</Badge>
						</div>
						<Separator />
						<div className="flex items-center justify-between">
							<span>Publish a rewrite</span>
							<Badge variant="outline" className="text-xs">+15 pts</Badge>
						</div>
						<Separator />
						<div className="flex items-center justify-between">
							<span>Receive an upvote</span>
							<Badge variant="outline" className="text-xs">+2 pts</Badge>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
